import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import useLocalStorage, { removeItem, STORAGE_KEYS } from "../hooks/useLocalStorage";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import Project from "../models/Project";
import ProjectService from "../services/ProjectService";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import { Routes } from "../types/Routes";
import RoleUtils from "../utils/RoleUtils";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import useUpdateUrl from "./useUpdateUrl";

export enum ProjectType {
  internal = "Internal",
  external = "External",
  both = "Internal + External",
}

export const projectKeys = new QueryKeyFactory(ProjectService.path);
const userKeys = new QueryKeyFactory(UserService.path);

const NO_NAMES: AutoCompleteUIDTO[] = [];
const NO_PROJECTS: Project[] = [];

const useProjectList = () => {
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [selectedEditProject, setSelectedEditProject] = useState<Project | undefined>(undefined);
  const [editProjectManager, setEditProjectManager] = useState<Project | undefined>(undefined);
  const [forceUpdateOn, setForceUpdateOn] = useState<number | undefined>(undefined);
  const [, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [managerList, setManagerList] = useState<EnsolversOpsUser[] | undefined>(undefined);
  const [searchManager, setSearchManager] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showArchivedProjects, setShowArchivedProjects] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_SHOW_ARCHIVED_PROJECTS, false);
  const [projectType, setProjectType] = useState<string>(ProjectType.both);

  const searchParams = new URLSearchParams(useLocation().search);
  const projectTypeFilter = searchParams.get("type");
  const assignedToMe = searchParams.get("assigned");
  const archived = searchParams.get("archived");
  const { updateUrl } = useUpdateUrl(Routes.PROJECTS, searchParams);

  useEffect(() => {
    if (projectTypeFilter !== null) {
      setProjectType(projectTypeFilter);
    } else {
      searchParams.set("type", ProjectType.both);
    }

    if (archived !== null) {
      setShowArchivedProjects(archived === "true");
    } else if (showArchivedProjects !== undefined) {
      searchParams.set("archived", showArchivedProjects.toString());
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userNamesQuery = useQuery({
    queryKey: userKeys.list({ scope: "manager-user-names" }),
    queryFn: () => unwrap(UserService.findManagerUserNames()),
    enabled: RoleUtils.isAdmin(),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const userNames = userNamesQuery.data ?? NO_NAMES;

  const projectsAssignedToPmQuery = useQuery({
    queryKey: projectKeys.list({ scope: "projects-from-manager" }),
    queryFn: () => unwrap(ProjectService.getProjectsFromManager()),
    enabled: RoleUtils.isManager(),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const projectsAssignedToPm = projectsAssignedToPmQuery.data ?? NO_PROJECTS;

  const projectIsAssignedToPm = (externalId: string): boolean => {
    return projectsAssignedToPm.some((project) => project.externalId === externalId);
  };

  const managersKey = (projectExternalId: string) => projectKeys.detail(`managers:${projectExternalId}`);

  const fetchManagerList = (projectExternalId: string, staleTime: number): Promise<EnsolversOpsUser[]> =>
    queryClient.fetchQuery({
      queryKey: managersKey(projectExternalId),
      queryFn: () => unwrap(ProjectService.getManagerListFromProject(projectExternalId)),
      staleTime,
    });

  /** Cell renderer reader: cached rows are fine here, mutations below keep the entry current. */
  const getManagerListFromProject = (projectExternalId: string): Promise<EnsolversOpsUser[]> =>
    fetchManagerList(projectExternalId, TABLE_QUERY_STALE_TIME);

  // Dialogs that act on the managers (assign / delete) always read the server list, never a cached one.
  useEffect(() => {
    if (!editProjectManager?.externalId) return;
    let cancelled = false;
    fetchManagerList(editProjectManager.externalId, 0)
      .then((list) => {
        if (!cancelled) setManagerList(list);
      })
      .catch((error) => traceo("Error getting project managers.", error, null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProjectManager]);

  useEffect(() => {
    if (!selectedProject?.externalId) return;
    let cancelled = false;
    fetchManagerList(selectedProject.externalId, 0)
      .then((list) => {
        if (!cancelled) setManagerList(list);
      })
      .catch((error) => traceo("Error getting project managers.", error, null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Project>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          projectKeys.list({
            page,
            pageSize,
            sortBy: sortBy ?? "",
            sortDir: sortDir ?? "",
            assigned,
            showArchivedProjects,
            projectType,
          }),
        queryFn: (page, pageSize) => {
          if (assigned) {
            return unwrap(ProjectService.getAllProjectsByManagerExternalId(page, pageSize, {}, showArchivedProjects, projectType));
          }
          return unwrap(ProjectService.findAll(page, pageSize, showArchivedProjects, projectType));
        },
        client: queryClient,
      }),
    [queryClient, assigned, showArchivedProjects, projectType],
  );

  const refreshTable = () => refreshTableQuery(projectKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const updateStoredProjectInStorage = (project: Project) => {
    setStoredProject({ externalId: project.externalId || "", label: project.name });
  };

  const createMutation = useMutation({
    mutationFn: (project: Project) => unwrap(ProjectService.create(project)),
    onSuccess: async (created) => {
      updateStoredProjectInStorage(created);
      await refreshTable();
      NotificationService.info("Project created successfully");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (project: Project) => unwrap(ProjectService.update(project)),
    onSuccess: async (updated) => {
      updateStoredProjectInStorage(updated);
      await refreshTable();
      NotificationService.info("Project edited successfully");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (project: Project) => unwrap(ProjectService.delete(project)),
    onSuccess: async (_, deleted) => {
      if (deleted.externalId) queryClient.removeQueries({ queryKey: managersKey(deleted.externalId) });
      setSelectedProject(undefined);
      setManagerList(undefined);
      await refreshTable();
      NotificationService.info("Project deleted successfully");
    },
  });

  /** Reload the project's managers from the server, refresh the open modal and the "Assigned Managers" column. */
  const syncManagers = async (projectExternalId: string) => {
    const list = await fetchManagerList(projectExternalId, 0);
    setManagerList(list);
    await refreshTable();
  };

  const addManagerMutation = useMutation({
    mutationFn: ({ projectExternalId, userExternalId }: { projectExternalId: string; userExternalId: string }) =>
      unwrap(ProjectService.addManagerToProject(projectExternalId, userExternalId)),
    onSuccess: async (_, { projectExternalId }) => {
      setSearchManager(undefined);
      await syncManagers(projectExternalId);
    },
  });

  const removeManagerMutation = useMutation({
    mutationFn: ({ projectExternalId, userExternalId }: { projectExternalId: string; userExternalId: string }) =>
      unwrap(ProjectService.removeManagerFromProject(projectExternalId, userExternalId)),
    onSuccess: (_, { projectExternalId }) => syncManagers(projectExternalId),
  });

  const handleAddManager = async () => {
    if (!searchManager) {
      NotificationService.error("You should first select a PM from the list.");
      return;
    }
    if (!editProjectManager?.externalId) return;
    try {
      await addManagerMutation.mutateAsync({
        projectExternalId: editProjectManager.externalId,
        userExternalId: searchManager.externalId,
      });
    } catch {
      // MutationCache handles ApiError notification.
    }
  };

  const handleRemoveManager = async (userExternalId: string) => {
    if (!editProjectManager?.externalId || !userExternalId) return;
    try {
      await removeManagerMutation.mutateAsync({
        projectExternalId: editProjectManager.externalId,
        userExternalId,
      });
    } catch {
      // MutationCache handles ApiError notification.
    }
  };

  const handleOnDeleteProject = async () => {
    if (!selectedProject) return;
    try {
      await deleteMutation.mutateAsync(selectedProject);
    } catch {
      // MutationCache handles ApiError notification.
    }
  };

  const onCreateProject = RoleUtils.isAdmin() ? (instance: Project) => createMutation.mutateAsync(instance).then(() => true) : undefined;

  const onUpdateProject = RoleUtils.isAdminOrManager()
    ? (instance: Project) => {
        setIsEditing(false);
        if (!instance.externalId || (!RoleUtils.isAdmin() && !projectIsAssignedToPm(instance.externalId))) {
          const user = AuthService.getUser();
          NotificationService.error(
            `Error updating a project, the project is not assigned and managed for user: ${user?.firstName} ${user?.lastName}`,
          );
          return Promise.reject(new Error("Project not assigned to manager"));
        }
        return updateMutation.mutateAsync(instance).then(() => true);
      }
    : undefined;

  const handleOpenAssignPM = (instance: Project) => {
    setEditProjectManager(instance);
  };

  const handleOpenDeleteDialog = (instance: Project) => {
    setSelectedProject(instance);
    removeItem(STORAGE_KEYS.STORED_PROJECT);
  };

  const handleOpenEditDialog = (instance: Project) => {
    setIsEditing(true);
    setSelectedEditProject(instance);
  };

  const handleCloseManagerModal = () => {
    setEditProjectManager(undefined);
    setSearchManager(undefined);
    setManagerList(undefined);
  };

  const handleOnCancel = () => {
    setIsEditing(false);
  };

  const handleChangeAssignedProjectsByUser = () => {
    setAssigned(!assigned);
    searchParams.set("assigned", (!assigned).toString());
    updateUrl();
  };

  const toggleShowArchivedProjects = () => {
    setShowArchivedProjects((prev) => !prev);
    searchParams.set("archived", (!showArchivedProjects).toString());
    updateUrl();
  };

  const handleChangeProjectType = (value: string) => {
    setProjectType(value);
    searchParams.set("type", value);
    updateUrl();
  };

  return {
    onDataFetch,
    forceUpdateOn,
    setForceUpdateOn: setForceUpdateOn as Dispatch<SetStateAction<number | undefined>>,
    refreshTable,
    isLoading: userNamesQuery.isFetching || projectsAssignedToPmQuery.isFetching,
    projectType,
    handleChangeProjectType,
    assigned,
    handleChangeAssignedProjectsByUser,
    showArchivedProjects,
    toggleShowArchivedProjects,
    selectedProject,
    setSelectedProject,
    selectedEditProject,
    setSelectedEditProject,
    editProjectManager,
    setEditProjectManager,
    isEditing,
    setIsEditing,
    managerList,
    setManagerList,
    searchManager,
    setSearchManager,
    userNames,
    handleCloseManagerModal,
    handleAddManager,
    handleRemoveManager,
    isUpdatingManagers: addManagerMutation.isPending || removeManagerMutation.isPending,
    handleOnCancel,
    getManagerListFromProject,
    handleOnDeleteProject,
    onCreateProject,
    onUpdateProject,
    createProject: createMutation.mutateAsync,
    updateProject: updateMutation.mutateAsync,
    handleOpenAssignPM,
    handleOpenDeleteDialog,
    handleOpenEditDialog,
    projectIsAssignedToPm,
  };
};

export default useProjectList;
